import { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";

import toast from "components/partials/toast/toast";

import { getTouchpointVersion } from "generated/api/touchpoint-version";
import type { BeeContentDTO } from "generated/schemas";

import { CampaignAttributes, useCreateCampaign, useCurrentCampaign } from "hooks/api/use-campaigns";

import { useParentTouchpointContext } from "components/modals/create-parent-touchpoint-modal/context/parent-touchpoint-context";
import {
  useGetCampaignObjectiveTypesQuery,
  useGetTouchpointContentTypesQuery,
  useGetProgramTypesQuery,
  useGetAudienceTypesQuery,
  useClassTypeOptions,
} from "hooks/api/dictionary";
import { OptionType } from "ui/select";

import { DEFAULT_TOUCHPOINT_VAL } from "components/modals/create-parent-touchpoint-modal/create-parent-touchpoint-modal.constants";
import { filterTouchpointClassesByAllowedTypes } from "components/modals/creative-modal/creative-modal-utils";

const TRANSITION_DELAY_MS = 400;
const EMAIL_CHANGE_DELAY_MS = 300;

export const useCreateParentTouchpointModal = () => {
  const {
    selectedVersion,
    defaultCampaign,
    currentTouchpoint,
    clientCampaigns,
    selectedCampaign,
    step,
    goTo,
    setSelectedCampaign,
    setIsGenerating,
    setGeneratedEmails,
    setCurrentEmailIndex,
    setIsTransitioning,
    setIsOpen,
    setGenerateError,
    setProgressText,
    abortControllerRef,
  } = useParentTouchpointContext();

  const {
    control,
    reset,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { errors },
  } = useFormContext();

  const currentContentType = watch("contentType");

  const currentCampaign = useCurrentCampaign();
  const createCampaignMutation = useCreateCampaign();

  const { data: contentTypes = [] } = useGetTouchpointContentTypesQuery(
    selectedCampaign.campaignObjective,
  );

  const contentTypeOptions: OptionType[] = useMemo(() => {
    return contentTypes.map((type) => ({
      label: type.description,
      value: type.name,
    }));
  }, [contentTypes]);
  const { data: programTypes = [] } = useGetProgramTypesQuery();
  const { data: audienceDictionaryItems = [] } = useGetAudienceTypesQuery();

  const getDefaultParentCampaignId = (
    defaultCampaign: CampaignAttributes,
    clientCampaigns: CampaignAttributes[],
  ): string => {
    const isDefaultInList = clientCampaigns.some((campaign) => campaign.id === defaultCampaign.id);
    if (isDefaultInList) {
      return defaultCampaign.id;
    }

    return clientCampaigns.length ? clientCampaigns[0].id : "";
  };

  const { classTypes, classTypeOptions } = useClassTypeOptions(selectedCampaign.campaignObjective);

  useEffect(() => {
    if (!classTypes.length) return;

    const filteredClasses = filterTouchpointClassesByAllowedTypes(getValues("classes"), classTypes);
    setValue("classes", filteredClasses, { shouldValidate: true });
  }, [classTypes, getValues, selectedCampaign.id, setValue]);

  useEffect(() => {
    if (!contentTypes.length) return;

    const isCurrentContentTypeValid =
      currentContentType && contentTypes.some((type) => type.name === currentContentType);

    if (!currentContentType || !isCurrentContentTypeValid) {
      if (contentTypes.length === 1) {
        setValue("contentType", contentTypes[0].name, { shouldValidate: true });
      } else if (!isCurrentContentTypeValid && currentContentType) {
        setValue("contentType", "", { shouldValidate: true });
      }
    }
  }, [contentTypes, currentContentType, setValue]);

  // Trigger validation when step-1 required fields are populated (including programmatic setValue updates).
  useEffect(() => {
    const values = getValues();
    if (
      values.campaignId &&
      values.touchpointName &&
      values.contentType &&
      values.classes?.length
    ) {
      trigger();
    }
  }, [getValues, trigger, currentContentType, classTypes, selectedCampaign.id]);

  const handleClose = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsGenerating(false);
    setProgressText("");
    setGeneratedEmails([]);
    setCurrentEmailIndex(0);
    setSelectedCampaign(defaultCampaign);
    goTo(1);
    reset();
    setIsOpen(false);
  };

  const handleStepChange = (newStep: number) => {
    if (newStep !== 3 && abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsGenerating(false);
      setProgressText("");
    }
    setGeneratedEmails([]);
    setCurrentEmailIndex(0);
    goTo(newStep);
  };

  const handleCampaignChange = (campaignId: string, onChange: (value: string) => void) => {
    const selectedCampaign =
      clientCampaigns.find((campaign) => campaign.id === campaignId) ?? defaultCampaign;
    setSelectedCampaign(selectedCampaign);
    onChange(selectedCampaign.id);

    // Update dependent field
    setValue("campaignObjective", selectedCampaign.campaignObjective);
    setValue("folderId", "");
    setValue("contentType", "");
  };

  const createNewParentCampaign = async () => {
    const data = getValues();
    try {
      const clientId = selectedCampaign.clientId || defaultCampaign.clientId || "";
      const campaignData = {
        programType: data.newCampaignProgramType,
        name: data.newCampaignName,
        cdnSubdirectoryName: data.newCampaignCdnSubdirectoryName || "",
        campaignObjective: data.newCampaignObjective,
        audiences: Array.from(new Set([...(data.newCampaignAudiences || []), "PARENTS"])),
        lastModifiedDate: DEFAULT_TOUCHPOINT_VAL,
      };

      const newCampaign = await createCampaignMutation.mutateAsync({
        clientId,
        data: campaignData,
      });
      clientCampaigns.push(newCampaign);
      setSelectedCampaign(newCampaign);
      reset({
        ...data,
        campaignId: newCampaign.id,
        touchpointName: data.touchpointName, // Keep the original touchpoint name
        campaignObjective: newCampaign.campaignObjective || "",
      });
      goTo(1);
      resetNewCampaignFields();
    } catch (err) {
      console.error("Campaign creation error:", err);
      toast.error({
        data: {
          title: "Failed to create parent campaign",
          message: "Please try again later.",
        },
      });
    }
  };

  const validateEmailPreview = (htmlContent: string, htmlJSON: string): void => {
    const parsedJson = JSON.parse(htmlJSON);
    if (!parsedJson?.page?.body) {
      throw new Error("Invalid Bee JSON structure");
    }

    // Test structure validation (same logic as EmailDesignPreview)
    const bodyMatch = htmlContent.match(/<body[^>]*>/g);
    if (!bodyMatch || bodyMatch.length === 0) {
      throw new Error("No body tag found in HTML");
    }
  };

  // handler to generate new email from current touchpoint (streaming SSE)
  const handleGenerateEmail = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setIsGenerating(true);
    setGenerateError(false);
    setProgressText("");
    if (step === 2) {
      setGeneratedEmails([]);
      goTo(3);
    }

    const transitionTimeout = setTimeout(() => {
      setIsTransitioning(false);
    }, TRANSITION_DELAY_MS);

    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;

    try {
      const touchpointVersion = getTouchpointVersion();

      await touchpointVersion.transformEmail(
        {
          clientId: defaultCampaign.clientId ?? "",
          campaignId: defaultCampaign.id,
          touchpointId: currentTouchpoint.id,
          versionId: selectedVersion?.id ?? "",
        },
        { emailPrompt: getValues("emailPrompt") },
        {
          signal,
          onChunk(chunk) {
            if (chunk.stage === "completed" && chunk.content) {
              try {
                const generatedMessage = JSON.parse(chunk.content) as BeeContentDTO;
                if (!generatedMessage?.htmlOutput || !generatedMessage?.jsonOutput) {
                  setGenerateError(true);
                  return;
                }
                validateEmailPreview(generatedMessage.htmlOutput, generatedMessage.jsonOutput);
                setGeneratedEmails((prev) => {
                  const nextEmails = [
                    ...prev,
                    {
                      html: generatedMessage.htmlOutput,
                      beeJson: generatedMessage.jsonOutput,
                    },
                  ];
                  setTimeout(() => handleEmailChange(nextEmails.length - 1), 0);
                  return nextEmails;
                });
                setProgressText("");
              } catch (error) {
                if (error instanceof Error && !error.message.includes("Invalid Bee")) {
                  console.error("Email preview validation failed:", error);
                  toast.error({
                    data: {
                      title: "Failed to generate email",
                      message: "Please try again later.",
                    },
                  });
                }
              }
            } else {
              setProgressText(chunk.content ?? "");
            }
          },
        },
      );

      if (signal.aborted) {
        if (abortControllerRef.current === controller) {
          setIsGenerating(false);
        }
        return;
      }
      if (abortControllerRef.current === controller) {
        setIsGenerating(false);
      }
    } catch (error) {
      if (error instanceof Error && error.name !== "AbortError") {
        console.error("Error generating parent email:", error);
        setGenerateError(true);
        toast.error({
          data: {
            title: "Failed to generate parent email",
            message: "Please try again later.",
          },
        });
      }
      if (abortControllerRef.current === controller) {
        setIsGenerating(false);
      }
    } finally {
      clearTimeout(transitionTimeout);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
        setProgressText("");
      }
    }
  };

  // handle switching between emails
  const handleEmailChange = (newIndex: number) => {
    setIsTransitioning(true);
    setCurrentEmailIndex(newIndex);
    // Clear any error state when navigating - error should not be saved in the stack
    setGenerateError(false);

    const emailChangeTimeout = setTimeout(() => {
      setIsTransitioning(false);
    }, EMAIL_CHANGE_DELAY_MS);
    return () => clearTimeout(emailChangeTimeout);
  };

  const { data: campaignObjectiveTypes } = useGetCampaignObjectiveTypesQuery(
    selectedCampaign?.programType,
  );

  const resetNewCampaignFields = () => {
    const defaultCampaign = currentCampaign;
    const defaultCampaignName = `${defaultCampaign.name} - P`;
    const defaultParentAudiences = Array.from(
      new Set([...(defaultCampaign.audiences || []), "PARENTS"]),
    );
    const defaultCdnSubdirectoryName = defaultCampaign.cdnSubdirectoryName
      ? `parent${defaultCampaign.cdnSubdirectoryName}`
      : "";

    setValue("newCampaignProgramType", defaultCampaign.programType || "ADMIT_TO_ENROLL");
    setValue("newCampaignName", defaultCampaignName);
    setValue("newCampaignCdnSubdirectoryName", defaultCdnSubdirectoryName);
    setValue("newCampaignObjective", defaultCampaign.campaignObjective || "APPLICATION_GENERATION");
    setValue("newCampaignAudiences", defaultParentAudiences);
  };

  return {
    // State
    contentTypes,
    contentTypeOptions,
    programTypes,
    audienceDictionaryItems,
    classTypeOptions,
    campaignObjectiveTypes,

    // Form
    control,
    reset,
    setValue,
    getValues,
    trigger,
    errors,

    // Handlers
    handleClose,
    handleStepChange,
    handleCampaignChange,
    createNewParentCampaign,
    handleGenerateEmail,
    handleEmailChange,
    getDefaultParentCampaignId,
    resetNewCampaignFields,
  };
};
